import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import TopNavigation from '@cloudscape-design/components/top-navigation';
import { getToken, getUserFromToken } from 'src/auth';
// Label + dropdown items derive from the single REGION_OPTIONS source of truth
// (src/constants) to avoid drift; it carries global search alongside the regions.
import { REGION_OPTIONS, getBadgePhotoUrl, regionLabel, withRegion } from 'src/constants';

interface BeaconHeaderProps {
  selectedRegion: string;
  onRegionChange: (regionId: string) => void;
}

export const BeaconHeader = ({
  selectedRegion,
  onRegionChange,
}: BeaconHeaderProps) => {
  const navigate = useNavigate();
  // Carries the region so returning home does not drop it from the URL.
  const homeHref = withRegion('/', selectedRegion);

  /**
   * Alias decoded from the Cognito ID token in sessionStorage on first
   * render. AuthGuard already guarantees a valid token before rendering
   * the app tree, so this is always populated in normal flow — no
   * fallback string needed.
   */
  const alias = useMemo(() => {
    const token = getToken();
    if (!token) return '';
    const user = getUserFromToken(token);
    return user?.alias ?? '';
  }, []);

  return (
    <div id="beacon-header">
      <style>{`
        #beacon-header [class*="top-navigation"] {
          background-color: #232f3e !important;
        }
        #beacon-header [class*="top-navigation"] [class*="identity"] span[class*="title"] {
          color: #ffffff !important;
        }
        #beacon-header [class*="top-navigation"] [class*="identity"] span[class*="title"]::after {
          content: ' Beacon';
          color: #fa9e14;
        }
      `}</style>
      <TopNavigation
        identity={{
          href: homeHref,
          title: 'Fintech |',
          onFollow: (e) => {
            e.preventDefault();
            navigate(homeHref);
          },
        }}
        utilities={[
          {
            type: 'menu-dropdown',
            text: regionLabel(selectedRegion),
            iconName: 'multiscreen',
            ariaLabel: 'Region selector',
            items: REGION_OPTIONS.map((r) => ({ id: r.value, text: r.label })),
            onItemClick: ({ detail }: { detail: { id: string } }) => onRegionChange(detail.id),
          },
          {
            type: 'button',
            text: alias,
            iconUrl: alias ? getBadgePhotoUrl(alias) : undefined,
            iconAlt: alias ? `${alias} photo` : 'User',
            ariaLabel: 'User',
          },
        ]}
      />
    </div>
  );
};
